import { useState, useEffect } from "react";
import { UiModal } from "../../../../Components/Shared/UiModal";

const EMPTY_FORM = {
  min_weight: "",
  max_weight: "",
  baseRate: "",
  zoneName: "",
};

export default function InStationModal({
  isOpen,
  onClose,
  onSubmit,
  editData,
  getAllInStationZones,
}) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [inStationZones, setInStationZones] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(EMPTY_FORM);
      setErrors({});
      return;
    }

    if (editData) {
      setFormData({
        min_weight: editData.min_weight || "",
        max_weight: editData.max_weight || "",
        baseRate: editData.base_rate || "",
        zoneName: editData.zone_name || editData.zone?.zone_name || "",
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
  }, [isOpen, editData]);

  useEffect(() => {
    if (isOpen && getAllInStationZones) {
      getAllInStationZones(setInStationZones);
    }
  }, [isOpen, getAllInStationZones]);

  useEffect(() => {
    if (editData && inStationZones.length > 0) {
      const zoneName = editData.zone_name || editData.zone?.zone_name;
      if (zoneName) {
        setFormData((prev) => ({
          ...prev,
          zoneName: zoneName,
        }));
      }
    }
  }, [editData, inStationZones]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "zoneName") {
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      const regex = /^\d*\.?\d*$/;
      if (regex.test(value) || value === "") {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.min_weight) {
      newErrors.min_weight = "Minimum Weight is required";
    }

    if (!formData.max_weight) {
      newErrors.max_weight = "Maximum Weight is required";
    }

    if (formData.min_weight && formData.max_weight) {
      const minWeight = parseFloat(formData.min_weight);
      const maxWeight = parseFloat(formData.max_weight);
      if (minWeight >= maxWeight) {
        newErrors.max_weight =
          "Maximum Weight must be greater than Minimum Weight";
      }
    }

    if (!formData.baseRate) {
      newErrors.baseRate = "Base Rate is required";
    }

    if (!formData.zoneName.trim()) {
      newErrors.zoneName = "Zone Name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const selectedZone = inStationZones.find(
        (zone) => zone.zone_name === formData.zoneName,
      );

      const submitData = {
        zone_id: selectedZone ? selectedZone.id : null,
        min_weight: parseFloat(formData.min_weight),
        max_weight: parseFloat(formData.max_weight),
        base_rate: parseFloat(formData.baseRate),
      };
      onSubmit(submitData);
    }
  };

  const isEditMode = Boolean(editData);
  const fieldClass = (name) =>
    `ui-input-field ${errors[name] ? "border-brand-error" : ""}`;

  return (
    <UiModal
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Delivery Management"
      title={
        isEditMode ? "Edit In-Station Base Rate" : "Add In-Station Base Rate"
      }
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="ui-btn-cancel min-w-[108px]"
          >
            Cancel
          </button>
          <button type="submit" className="ui-btn-save min-w-[108px]">
            {isEditMode ? "Update" : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Rate details</h4>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="ui-form-label">
                Zone Name <span className="text-red-500">*</span>
              </label>
              <select
                name="zoneName"
                value={formData.zoneName}
                onChange={handleInputChange}
                disabled={Boolean(editData)}
                className={`ui-select-field ${
                  errors.zoneName ? "border-brand-error" : ""
                } ${editData ? "cursor-not-allowed bg-gray-100" : ""}`}
              >
                <option value="">Select a zone</option>
                {inStationZones && inStationZones.length > 0 ? (
                  inStationZones.map((zone) => (
                    <option key={zone.id} value={zone.zone_name}>
                      {zone.zone_name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    No zones available
                  </option>
                )}
              </select>
              {errors.zoneName ? (
                <p className="ui-form-error">{errors.zoneName}</p>
              ) : null}
            </div>
            <div>
              <label className="ui-form-label">
                Base Rate <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="baseRate"
                value={formData.baseRate}
                onChange={handleInputChange}
                className={fieldClass("baseRate")}
                placeholder="e.g., 1, 2, 3.4"
              />
              {errors.baseRate ? (
                <p className="ui-form-error">{errors.baseRate}</p>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="ui-form-label">
                Minimum Weight <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="min_weight"
                value={formData.min_weight}
                onChange={handleInputChange}
                className={fieldClass("min_weight")}
                placeholder="e.g., 1, 2, 3.4"
              />
              {errors.min_weight ? (
                <p className="ui-form-error">{errors.min_weight}</p>
              ) : null}
            </div>
            <div>
              <label className="ui-form-label">
                Maximum Weight <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="max_weight"
                value={formData.max_weight}
                onChange={handleInputChange}
                className={fieldClass("max_weight")}
                placeholder="e.g., 1, 2, 3.4"
              />
              {errors.max_weight ? (
                <p className="ui-form-error">{errors.max_weight}</p>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </UiModal>
  );
}
